import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { ContactHit, OptInMethod, Uuid } from '@zenda/api-contract';
import { ClientTag } from './ClientTag';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { METODOS_DE_OPT_IN } from './NovoPaciente';
import { useAbrirConversa, usePatientMutations } from '../lib/queries';
import { formatPhone } from '../lib/format';
import { useT } from '../lib/i18n';
import { rotaDaConversa } from '../lib/rotas';

/** Abre a conversa do paciente com o médico, vazia se for a primeira, e vai até ela. */
export function useIrParaConversa(): {
  readonly ir: (clientId: Uuid, contactId: Uuid, depois?: () => void) => void;
  readonly indo: boolean;
} {
  const navigate = useNavigate();
  const abrir = useAbrirConversa();
  return {
    ir: (clientId, contactId, depois) =>
      abrir.mutate(
        { clientId, contactId },
        {
          onSuccess: (r) => {
            depois?.();
            navigate(rotaDaConversa(r.conversationId));
          },
        },
      ),
    indo: abrir.isPending,
  };
}

/** Paciente de mais de um médico: a atendente escolhe por qual número vai escrever. */
export function NovaConversaModal({
  contato,
  onClose,
}: {
  readonly contato: ContactHit;
  readonly onClose: () => void;
}): ReactElement {
  const t = useT();
  const { ir, indo } = useIrParaConversa();
  return (
    <Modal
      title={t.novaConversa.titulo(contato.name)}
      subtitle={contato.phone === null ? t.novaConversa.subtitulo : formatPhone(contato.phone)}
      onClose={onClose}
    >
      <div className="nova-conversa">
        <p className="form__hint">{t.novaConversa.qualMedicoHint}</p>
        <div className="nova-conversa__opcoes">
          {contato.clients.map((c) => (
            <button
              key={c.clientId}
              type="button"
              className="nova-conversa__opcao"
              disabled={indo}
              onClick={() => ir(c.clientId, contato.contactId, onClose)}
            >
              <ClientTag name={c.clientName} color={c.clientColor} />
              <span className="nova-conversa__acao">
                {c.conversationId === null ? t.novaConversa.comecar : t.novaConversa.abrirConversa}
                <Icon name="chevron-right" size={14} />
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

/**
 * O passo antes do primeiro template: registrar que o paciente autorizou.
 *
 * Mensagem iniciada pela empresa exige autorização do paciente ✅
 * (developers.facebook.com/docs/whatsapp/overview/getting-opt-in).
 */
export function AutorizacaoNaConversa({
  clientId,
  contactId,
  conversationId,
}: {
  readonly clientId: Uuid;
  readonly contactId: Uuid;
  readonly conversationId: Uuid;
}): ReactElement {
  const t = useT();
  const client = useQueryClient();
  const [metodo, setMetodo] = useState<OptInMethod>('presencial');
  const { save } = usePatientMutations(clientId, contactId);

  return (
    <div className="nova-conversa__optin">
      <p className="nova-conversa__titulo">{t.novaConversa.precisaAutorizacao}</p>
      <p className="form__hint">{t.novaConversa.porQue}</p>
      <div className="form__row">
        <select
          className="select select--sm"
          aria-label={t.patients.optIn.method}
          value={metodo}
          onChange={(e) => setMetodo(e.target.value as OptInMethod)}
        >
          {METODOS_DE_OPT_IN.map((m) => (
            <option key={m} value={m}>
              {t.patients.optIn.methods[m]}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { optInMethod: metodo },
              {
                onSuccess: () =>
                  void client.invalidateQueries({ queryKey: ['conversation', conversationId] }),
              },
            )
          }
        >
          {t.novaConversa.registrarEContinuar}
        </button>
      </div>
      {save.isError ? <p className="form__error">{save.error.message}</p> : null}
    </div>
  );
}
